import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
  XMarkIcon,
  UserCircleIcon,
  EnvelopeIcon,
  CalendarIcon,
  AcademicCapIcon,
  TrophyIcon,
  FireIcon,
  ClockIcon,
  CheckCircleIcon,
  BookOpenIcon,
  BeakerIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline'

export default function UserDetailModal({ user, isOpen, onClose }) {
  if (!user) return null

  const formatDate = (date) => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatTime = (minutes) => {
    if (!minutes) return '0m'
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours === 0) return `${mins}m`
    if (mins === 0) return `${hours}h`
    return `${hours}h ${mins}m`
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-8">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center text-blue-600 font-bold text-2xl">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="text-white">
                        <h3 className="text-2xl font-bold">{user.name}</h3>
                        <p className="text-blue-100 flex items-center gap-2 mt-1">
                          <EnvelopeIcon className="w-4 h-4" />
                          {user.email}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="text-white hover:text-gray-200 transition-colors"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-4 gap-4 mt-6">
                    <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
                      <p className="text-white/80 text-xs uppercase tracking-wide">Role</p>
                      <p className="text-white font-bold text-lg capitalize mt-1">{user.role}</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
                      <p className="text-white/80 text-xs uppercase tracking-wide">Status</p>
                      <p className={`font-bold text-lg mt-1 ${user.is_active ? 'text-green-300' : 'text-red-300'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
                      <p className="text-white/80 text-xs uppercase tracking-wide">Streak</p>
                      <p className="text-white font-bold text-lg mt-1 flex items-center justify-center gap-1">
                        <FireIcon className="w-5 h-5 text-orange-300" />
                        {user.current_streak || 0}
                      </p>
                    </div>
                    <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
                      <p className="text-white/80 text-xs uppercase tracking-wide">Progress</p>
                      <p className="text-white font-bold text-lg mt-1">
                        {user.completed_count || 0}/{user.progress_count || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Account Information */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <UserCircleIcon className="w-5 h-5 text-blue-600" />
                        Account Information
                      </h4>
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-xs text-gray-500 uppercase tracking-wide">User ID</dt>
                          <dd className="text-sm font-medium text-gray-900 mt-1">#{user.id}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500 uppercase tracking-wide">Joined</dt>
                          <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center gap-2">
                            <CalendarIcon className="w-4 h-4 text-gray-400" />
                            {formatDate(user.created_at)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500 uppercase tracking-wide">Last Login</dt>
                          <dd className="text-sm font-medium text-gray-900 mt-1">
                            {formatDate(user.last_login_at) || 'Never'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500 uppercase tracking-wide">Email Verified</dt>
                          <dd className="text-sm font-medium text-gray-900 mt-1">
                            {user.email_verified_at ? (
                              <span className="text-green-600 flex items-center gap-1">
                                <CheckCircleIcon className="w-4 h-4" />
                                Verified
                              </span>
                            ) : (
                              <span className="text-gray-400">Not verified</span>
                            )}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Enrollment & Purchases */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <AcademicCapIcon className="w-5 h-5 text-purple-600" />
                        Enrollment & Purchases
                      </h4>
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-xs text-gray-500 uppercase tracking-wide">Enrolled</dt>
                          <dd className="text-sm font-medium text-gray-900 mt-1">
                            {user.is_enrolled ? (
                              <span className="text-green-600 flex items-center gap-1">
                                <CheckCircleIcon className="w-4 h-4" />
                                Yes
                              </span>
                            ) : (
                              <span className="text-gray-400">No</span>
                            )}
                          </dd>
                        </div>
                        {user.is_enrolled && (
                          <>
                            <div>
                              <dt className="text-xs text-gray-500 uppercase tracking-wide">Enrolled Tier</dt>
                              <dd className="text-sm font-medium text-gray-900 mt-1 capitalize">
                                {user.enrolled_tier || 'N/A'}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs text-gray-500 uppercase tracking-wide">Enrolled Date</dt>
                              <dd className="text-sm font-medium text-gray-900 mt-1">
                                {formatDate(user.enrolled_at)}
                              </dd>
                            </div>
                          </>
                        )}
                        <div>
                          <dt className="text-xs text-gray-500 uppercase tracking-wide">Purchases</dt>
                          <dd className="text-sm font-medium text-gray-900 mt-1">
                            {user.purchases && user.purchases.length > 0 ? (
                              <div className="space-y-1">
                                {user.purchases.map((purchase) => (
                                  <div key={purchase.id} className="text-xs">
                                    <span className="capitalize font-semibold">{purchase.tier}</span> - 
                                    ${purchase.amount_dollars?.toFixed(2)} 
                                    <span className={`ml-2 px-2 py-0.5 rounded-full ${
                                      purchase.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {purchase.status}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">No purchases</span>
                            )}
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Learning Progress */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <BookOpenIcon className="w-5 h-5 text-green-600" />
                        Learning Progress
                      </h4>
                      <div className="space-y-4">
                        {/* Modules */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <BookOpenIcon className="w-4 h-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Modules</p>
                              <p className="text-xs text-gray-500">
                                {user.modules_completed || 0} completed
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-blue-600">{user.modules_completed || 0}</p>
                          </div>
                        </div>

                        {/* Labs */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <BeakerIcon className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Labs</p>
                              <p className="text-xs text-gray-500">
                                {user.labs_completed || 0} completed
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-purple-600">{user.labs_completed || 0}</p>
                          </div>
                        </div>

                        {/* Capstones */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                              <RocketLaunchIcon className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Capstones</p>
                              <p className="text-xs text-gray-500">
                                {user.capstones_completed || 0} completed
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">{user.capstones_completed || 0}</p>
                          </div>
                        </div>

                        {/* Overall Progress Bar */}
                        <div className="pt-3 border-t border-gray-200">
                          <div className="flex justify-between text-xs text-gray-600 mb-2">
                            <span>Overall Progress</span>
                            <span className="font-semibold">
                              {user.progress_count > 0 
                                ? Math.round((user.completed_count / user.progress_count) * 100)
                                : 0}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-500"
                              style={{
                                width: user.progress_count > 0 
                                  ? `${Math.round((user.completed_count / user.progress_count) * 100)}%`
                                  : '0%'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Achievements & Stats */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <TrophyIcon className="w-5 h-5 text-yellow-600" />
                        Achievements & Stats
                      </h4>
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-xs text-gray-500 uppercase tracking-wide">Current Streak</dt>
                          <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center gap-2">
                            <FireIcon className="w-5 h-5 text-orange-500" />
                            <span className="text-lg font-bold">{user.current_streak || 0}</span>
                            <span className="text-gray-500">days</span>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500 uppercase tracking-wide">Longest Streak</dt>
                          <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center gap-2">
                            <TrophyIcon className="w-5 h-5 text-yellow-500" />
                            <span className="text-lg font-bold">{user.longest_streak || 0}</span>
                            <span className="text-gray-500">days</span>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500 uppercase tracking-wide">Total Time Logged</dt>
                          <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center gap-2">
                            <ClockIcon className="w-5 h-5 text-blue-500" />
                            <span className="text-lg font-bold">{formatTime(user.total_time_minutes || 0)}</span>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-gray-500 uppercase tracking-wide">Certificates Earned</dt>
                          <dd className="text-sm font-medium text-gray-900 mt-1 flex items-center gap-2">
                            <AcademicCapIcon className="w-5 h-5 text-purple-500" />
                            <span className="text-lg font-bold">{user.certificates_count || 0}</span>
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
